import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../common/Toast';
import { getTicketByNo } from '../../api/tickets';
import { getWorkItemProjectRef, getWorkItemByNo } from '../../api/workItems';
import { getAllCompanies } from '../../api/companies';
import { parseWorkItemRef, setWorkItemRefById, setWorkItemRefByNo, clearWorkItemRef } from '../../utils/workItemUrlParams';
import { buildUrlParams, parseUrlParams } from '../../utils/betaUrlParams';
import BetaSidebar from './BetaSidebar';
import BetaTopBar from './BetaTopBar';
import WorkItemApp from '../workitem/WorkItemApp';
import TicketListView from './views/TicketListView';
import GlobalSearchView from './views/GlobalSearchView';
import BetaTimelineView from './views/BetaTimelineView';
import BetaBoardView from './views/BetaBoardView';
import BetaMilestoneView from './views/BetaMilestoneView';
import TicketDetailPage from './tickets/TicketDetailPage';
// import TicketCreateModal from './tickets/TicketCreateModal'; // 全面封鎖建立 Ticket,先隱藏
import TodoCreateModal from './tickets/TodoCreateModal';
import BetaDailyReportView from './views/BetaDailyReportView';
import { hasResumableHosting } from '../../hooks/useDailyReportSession';
import WorkItemWelcomeModal from '../workitem/onboarding/WorkItemWelcomeModal';
import ExportModal from './ExportModal';
import GlobalCompanySwitcher from '../common/GlobalCompanySwitcher';
import Marquee from '../common/Marquee';
import { isNewWorkItemUI } from '../../constants/cutover';
import './BetaLayout.scss';

const SIDEBAR_MIN_WIDTH = 200;
const SIDEBAR_DEFAULT_WIDTH = 280;

const BetaLayout = () => {
  const { profile, viewingCompanyId } = useAuth();
  const toast = useToast();
  const companyId = viewingCompanyId || profile?.company_id;
  const [searchParams, setSearchParams] = useSearchParams();
  // 轉換期預設新版,僅 ?v=old 看舊版;轉換後恆新版(見 constants/cutover)
  const showNewUI = isNewWorkItemUI(searchParams);
  // topbar「新增工作項目」→ 通知 WorkItemApp 開建立視窗
  const [wiCreateNonce, setWiCreateNonce] = useState(0);

  // ── 網址寫入的唯一入口 ──
  // 一律走 react-router 的 setSearchParams,絕不直接動 window.history。直接動的話
  // router 的 searchParams 會落後於網址列,變成兩個真相來源:讀 searchParams 的
  // effect(工作項目 deep-link、?v= 新舊 UI 切換)會拿到過期的值,而網址列上該有的
  // 參數已經不見了。狀態也不再存進 history.state,上/下一頁一律從網址重建。
  const pushUrl = useCallback((state) => {
    const params = buildUrlParams(state);
    if (state.clearWorkItem) clearWorkItemRef(params);
    setSearchParams(params);
  }, [setSearchParams]);
  const replaceUrl = useCallback((state) => {
    const params = buildUrlParams(state);
    if (state.clearWorkItem) clearWorkItemRef(params);
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  // 從 URL params 讀取初始狀態
  const initialUrl = useRef(parseUrlParams());
  const [currentView, setCurrentView] = useState(initialUrl.current.view);
  const [selectedProjectId, setSelectedProjectId] = useState(initialUrl.current.projectId);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  // const [showCreateModal, setShowCreateModal] = useState(false); // 全面封鎖建立 Ticket,先隱藏
  const [showTodoModal, setShowTodoModal] = useState(false);
  // 重新整理前正在主持簡報 → 自動打開 DailyReport 接著主持
  const [showDailyReport, setShowDailyReport] = useState(() => hasResumableHosting());
  const [showExportModal, setShowExportModal] = useState(false);
  const [showWiWelcome, setShowWiWelcome] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    try {
      const saved = localStorage.getItem('beta-sidebar-width');
      const parsed = saved ? Number(saved) : NaN;
      return parsed >= SIDEBAR_MIN_WIDTH ? parsed : SIDEBAR_DEFAULT_WIDTH;
    } catch { return SIDEBAR_DEFAULT_WIDTH; }
  });
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef({ startX: 0, startWidth: 0 });
  const marqueeRef = useRef(null);
  const [marqueeHeight, setMarqueeHeight] = useState(0);

  // 首次進新版工作項目 UI → 自動彈歡迎導覽(新 key wi_tour_completed,不碰舊 beta_tour_completed)
  useEffect(() => {
    if (showNewUI && profile?.id && !profile.settings?.wi_tour_completed) {
      setShowWiWelcome(true);
    }
  }, [showNewUI, profile?.id, profile?.settings?.wi_tour_completed]);

  // 測量跑馬燈高度，同步給 sidebar 用的 CSS 變數
  useEffect(() => {
    const el = marqueeRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setMarqueeHeight(Math.round(entry.contentRect.height));
    });
    ro.observe(el);
    // 初始測量
    setMarqueeHeight(Math.round(el.getBoundingClientRect().height));
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const onMove = (e) => {
      const diff = e.clientX - resizeRef.current.startX;
      const maxWidth = window.innerWidth * 0.9;
      const newWidth = Math.min(Math.max(resizeRef.current.startWidth + diff, SIDEBAR_MIN_WIDTH), maxWidth);
      setSidebarWidth(newWidth);
    };

    const onUp = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isResizing]);

  // 拖曳結束時把寬度存入 localStorage
  const prevIsResizing = useRef(false);
  useEffect(() => {
    if (prevIsResizing.current && !isResizing) {
      try { localStorage.setItem('beta-sidebar-width', String(sidebarWidth)); } catch {}
    }
    prevIsResizing.current = isResizing;
  }, [isResizing, sidebarWidth]);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    resizeRef.current = { startX: e.clientX, startWidth: sidebarWidth };
    setIsResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [sidebarWidth]);

  // Ticket drawer（右側滑入面板）
  const [drawerTicketId, setDrawerTicketId] = useState(null);
  // 列表刷新 key — 建立/編輯 ticket 後遞增以觸發重新載入
  const [listRefreshKey, setListRefreshKey] = useState(0);

  const [initialPhaseId, setInitialPhaseId] = useState(null);

  // ── 公司 name ↔ id 映射（供 URL cid 解析 / 產生用） ──
  const companiesRef = useRef({ byName: {}, byId: {}, loaded: false });

  const ensureCompaniesLoaded = useCallback(async () => {
    if (companiesRef.current.loaded) return companiesRef.current;
    const { data } = await getAllCompanies();
    const byName = {};
    const byId = {};
    (data || []).forEach((c) => {
      const slug = c.name.toLowerCase().trim();
      byName[slug] = c.id;
      byId[c.id] = slug;
    });
    companiesRef.current = { byName, byId, loaded: true };
    return companiesRef.current;
  }, []);

  // ── URL 初始化：若 URL 帶 ?ticket=123，解析並開啟 drawer ──
  const urlInitDone = useRef(false);
  useEffect(() => {
    if (urlInitDone.current) return;
    if (!companyId) return;
    urlInitDone.current = true;

    const { ticketNo, cid: cidParam } = initialUrl.current;
    if (ticketNo == null) {
      replaceUrl({ view: initialUrl.current.view, projectId: initialUrl.current.projectId });
      return;
    }

    const isSuperAdmin = profile?.role === 'super_admin';
    const resolve = async () => {
      const companies = await ensureCompaniesLoaded();
      // cid 是公司名稱（如 "sonice"），轉成 UUID；沒帶 cid 就用當前公司
      const lookupCompanyId = cidParam ? (companies.byName[cidParam.toLowerCase()] || null) : companyId;

      if (!lookupCompanyId) {
        toast.warning(`找不到公司「${cidParam}」`);
        replaceUrl({ view: initialUrl.current.view, projectId: initialUrl.current.projectId });
        return;
      }

      // 非 super_admin 不能查其他公司的 ticket
      if (lookupCompanyId !== companyId && !isSuperAdmin) {
        toast.warning('無權限查看其他公司的 Ticket');
        replaceUrl({ view: initialUrl.current.view, projectId: initialUrl.current.projectId });
        return;
      }

      const { data } = await getTicketByNo(ticketNo, lookupCompanyId);
      if (data) {
        setDrawerTicketId(data.id);
        // 切換到該 ticket 所屬專案
        if (data.project_id) {
          setCurrentView('project');
          setSelectedProjectId(data.project_id);
        }
        const companySlug = companies.byId[lookupCompanyId] || cidParam;
        replaceUrl({ view: data.project_id ? 'project' : 'my-tasks', projectId: data.project_id || null, ticketNo, cid: companySlug });
      } else {
        toast.warning(`Ticket #${ticketNo} 不存在或無權限查看`);
        replaceUrl({ view: initialUrl.current.view, projectId: initialUrl.current.projectId });
      }
    };
    resolve();
  }, [companyId, ensureCompaniesLoaded, toast, profile?.role]);

  // ── work_item deep-link → 左側 sidebar 同步選到該項目所屬專案 ──
  // drawer 本身由 WorkItemApp 開;這裡只補「背景列表/選取跟著切」(複製連結、通知、日報點擊共用)。
  // 參數怎麼判讀一律交給 utils/workItemUrlParams,這裡不自行從 wi/cid 推語義。
  const wiParam = searchParams.get('wi');
  const winoParam = searchParams.get('wino');
  const cidParam = searchParams.get('cid');
  useEffect(() => {
    if (!companyId) return undefined;
    const ref = parseWorkItemRef(searchParams);
    if (!ref) return undefined;
    let cancelled = false;
    const applyRef = (data) => {
      if (cancelled || !data?.project_id) return;
      if (data.company_id && data.company_id !== companyId) return; // 他公司項目不動 sidebar
      setCurrentView('project');
      setSelectedProjectId(data.project_id);
      // 網址要能自我描述:補上 view/projectId(否則上/下一頁回到這一步時 sidebar 會空掉,
      // 因為狀態只從網址重建),並把 ?wi=<id> / LEGACY 的 ?cid&wi=<編號> 正規化成
      // ?cid=<slug>&wino=<編號>,讓網址編號跟畫面 #號一致。
      // 子項沒有 work_item_no,無號可正規化就維持 id 形式。
      const needNormalize = ref.by === 'id' || ref.legacy;
      ensureCompaniesLoaded().then((companies) => {
        if (cancelled) return;
        const sp = new URLSearchParams(window.location.search);
        const slug = companies.byId[data.company_id || companyId];
        const no = ref.by === 'no' ? ref.no : data.work_item_no;
        if (needNormalize && slug && no != null) setWorkItemRefByNo(sp, no, slug, { subId: ref.subId });
        sp.set('view', 'project');
        sp.set('projectId', String(data.project_id));
        // 沒有實際變動就不要寫,避免多一次 render / history 動作
        if (sp.toString() !== window.location.search.replace(/^\?/, '')) setSearchParams(sp, { replace: true });
      });
    };
    if (ref.by === 'no') {
      // cid slug → company_id(mirror Ticket 的 byName),再用 (公司,編號) 解 work_item
      ensureCompaniesLoaded().then((companies) => {
        if (cancelled) return;
        const target = companies.byName[(ref.cid || '').toLowerCase().trim()];
        if (target) getWorkItemByNo(ref.no, target).then(({ data }) => applyRef(data));
      });
    } else {
      getWorkItemProjectRef(ref.id).then(({ data }) => applyRef(data));
    }
    return () => { cancelled = true; };
  }, [wiParam, winoParam, cidParam, companyId]);

  // ── 瀏覽器上/下一頁 ──
  // 一律從網址重建,不讀 history.state。history.state 以前是第二個真相來源
  // (pushUrl 自己塞的物件),跟網址、跟 router 的 searchParams 三方可能不一致。
  // 公司改用網址上的 cid 解析,跟進站時的解法同一套。
  useEffect(() => {
    const handlePopState = () => {
      const { view, projectId, ticketNo, cid } = parseUrlParams();
      setCurrentView(view);
      setSelectedProjectId(projectId);
      if (ticketNo == null) { setDrawerTicketId(null); return; }
      const isSA = profile?.role === 'super_admin';
      ensureCompaniesLoaded().then((companies) => {
        const lookupId = cid ? (companies.byName[cid.toLowerCase().trim()] || null) : companyId;
        if (!lookupId || (lookupId !== companyId && !isSA)) { setDrawerTicketId(null); return; }
        getTicketByNo(ticketNo, lookupId).then(({ data }) => setDrawerTicketId(data?.id || null));
      });
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [companyId, ensureCompaniesLoaded, profile?.role]);

  // handleNavigate 第 4 參數 extra: { ticketNo } 用於 URL 同步
  const handleNavigate = (view, projectId = null, ticketId = null, extra = {}) => {
    if (view === 'create-ticket') {
      // 全面封鎖建立 Ticket:入口按鈕已隱藏,這裡一併擋掉避免殘留路徑喚起
      // setShowCreateModal(true);
      return;
    }
    if (view === 'create-todo') {
      setShowTodoModal(true);
      return;
    }
    if (view === 'daily-report') {
      setShowDailyReport(true);
      return;
    }
    if (view === 'replay-wi-tour') {
      setShowWiWelcome(true);
      return;
    }
    if (view === 'export') {
      setShowExportModal(true);
      return;
    }
    // Work Item(新版)→ 切到新版 UI 並用 deep-link 參數開啟 drawer
    if (view === 'work_item' && ticketId) {
      // ticketId 在此用途是 work_item 的資料庫 id。殘留參數(cid/ticket/wino)的清理
      // 由 setWorkItemRefById 統一負責,呼叫端不需要也不該自己記得清。
      // 子項:extra.subId → wisub,WorkItemApp 會開母項 drawer 並彈出該子項 card。
      const sp = setWorkItemRefById(new URLSearchParams(window.location.search), ticketId, { subId: extra.subId ?? null });
      sp.set('v', 'new');
      setSearchParams(sp);
      return;
    }
    // Ticket → 右側 drawer，不切換 view
    if (view === 'ticket' && ticketId) {
      setDrawerTicketId(ticketId);
      if (extra.ticketNo != null) {
        ensureCompaniesLoaded().then((companies) => {
          const cid = companies.byId[companyId] || null;
          pushUrl({ view: currentView, projectId: selectedProjectId, ticketNo: extra.ticketNo, cid, clearWorkItem: true });
        });
      }
      return;
    }
    // 從 Milestone 點進某 Phase → 導覽到該專案的 Phases Tab，自動展開該 Phase
    if (view === 'project-phases') {
      setCurrentView('project-phases');
      setSelectedProjectId(projectId);
      setInitialPhaseId(ticketId); // ticketId 在此用途是 phaseId
      setMobileSidebarOpen(false);
      pushUrl({ view: 'project-phases', projectId, clearWorkItem: true });
      return;
    }
    setInitialPhaseId(null);
    setCurrentView(view);
    setSelectedProjectId(projectId);
    setMobileSidebarOpen(false);
    pushUrl({ view, projectId, clearWorkItem: true });
  };

  const renderView = () => {
    switch (currentView) {
      case 'my-tasks':
        return <TicketListView key="my-tasks" onNavigate={handleNavigate} refreshKey={listRefreshKey} />;
      case 'global-search':
        return <GlobalSearchView onNavigate={handleNavigate} refreshKey={listRefreshKey} />;
      case 'global-ticket-search':
        // 轉換後新版模式下查閱過往 Ticket(重用舊版全系統 Ticket 搜尋)
        return <GlobalSearchView onNavigate={handleNavigate} refreshKey={listRefreshKey} />;
      case 'project':
        return <TicketListView key={`project-${selectedProjectId}`} projectId={selectedProjectId} onNavigate={handleNavigate} refreshKey={listRefreshKey} />;
      case 'project-phases':
        return <TicketListView key={`phases-${selectedProjectId}`} projectId={selectedProjectId} onNavigate={handleNavigate} refreshKey={listRefreshKey} initialTab="phases" initialPhaseId={initialPhaseId} />;
      case 'timeline':
        return <BetaTimelineView onNavigate={handleNavigate} />;
      case 'board':
        return <BetaBoardView onNavigate={handleNavigate} />;
      case 'milestone':
        return <BetaMilestoneView onNavigate={handleNavigate} />;
      default:
        return <TicketListView onNavigate={handleNavigate} refreshKey={listRefreshKey} />;
    }
  };

  const effectiveWidth = sidebarCollapsed ? 60 : sidebarWidth;

  return (
    <div
      className={`beta-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${isResizing ? 'is-resizing' : ''}`}
      style={{ '--sidebar-w': `${effectiveWidth}px`, '--marquee-h': `${marqueeHeight}px` }}
    >
      {/* 全寬跑馬燈 / 裝飾垂簾 */}
      <div ref={marqueeRef}>
        <Marquee />
      </div>

      <div className="beta-layout-body">
        {mobileSidebarOpen && (
          <div
            className="beta-sidebar-overlay"
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}

        <BetaSidebar
          currentView={currentView}
          selectedProjectId={selectedProjectId}
          collapsed={sidebarCollapsed}
          mobileOpen={mobileSidebarOpen}
          onNavigate={handleNavigate}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          width={effectiveWidth}
          onResizeStart={handleResizeStart}
        />

        <div className="beta-main">
          <BetaTopBar
            currentView={currentView}
            selectedProjectId={selectedProjectId}
            onMenuClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
            onNavigate={handleNavigate}
            onRequestCreateWorkItem={() => setWiCreateNonce((n) => n + 1)}
          />

          {showNewUI && currentView !== 'global-ticket-search' ? (
            <div className="beta-view-content beta-view-content--new">
              <WorkItemApp projectId={selectedProjectId} createNonce={wiCreateNonce} currentView={currentView} />
            </div>
          ) : (
            <div className="beta-view-content" data-tour="ticket-list-area">
              {renderView()}
            </div>
          )}
        </div>

        {/* Ticket Detail Drawer */}
        {drawerTicketId && (
          <>
            <div className="ticket-drawer-overlay" onClick={() => { setDrawerTicketId(null); setListRefreshKey((k) => k + 1); pushUrl({ view: currentView, projectId: selectedProjectId }); }} />
            <div className="ticket-drawer">
              <TicketDetailPage
                ticketId={drawerTicketId}
                onClose={() => { setDrawerTicketId(null); setListRefreshKey((k) => k + 1); pushUrl({ view: currentView, projectId: selectedProjectId }); }}
                onNavigate={handleNavigate}
              />
            </div>
          </>
        )}

        {/* Create Ticket Modal — 全面封鎖建立 Ticket,整個 modal 先隱藏 */}
        {/*
        <TicketCreateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          projectId={selectedProjectId}
          onSuccess={(ticket) => {
            setDrawerTicketId(ticket.id);
            setListRefreshKey((k) => k + 1);
            if (ticket.ticket_no) {
              const cid = companiesRef.current.byId[companyId] || null;
              pushUrl({ view: currentView, projectId: selectedProjectId, ticketNo: ticket.ticket_no, cid, clearWorkItem: true });
            }
          }}
        />
        */}

        {/* Create ToDo Modal */}
        <TodoCreateModal
          isOpen={showTodoModal}
          onClose={() => setShowTodoModal(false)}
          onSuccess={() => {
            setShowTodoModal(false);
          }}
        />

        {/* Daily Report Modal */}
        {showDailyReport && (
          <BetaDailyReportView
            onClose={() => setShowDailyReport(false)}
            onNavigate={handleNavigate}
          />
        )}

        {/* Export Modal */}
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
        />


        {showNewUI && (
          <WorkItemWelcomeModal
            open={showWiWelcome}
            onClose={() => setShowWiWelcome(false)}
          />
        )}

        <GlobalCompanySwitcher />
      </div>
    </div>
  );
};

export default BetaLayout;
